import { useEffect } from 'react';
import { getBaseUrl } from '../../lib/utils';
import { siteConfig } from '../../config/site';

interface SEOProps {
  title?: string;
  description?: string;
  canonical?: string;
  ogType?: 'website' | 'article';
  ogImage?: string;
  jsonLd?: object;
  noIndex?: boolean;
  article?: {
    publishedTime?: string;
    modifiedTime?: string;
    author?: string;
    tags?: string[];
  };
}

export const SEO = ({
  title,
  description = siteConfig.description,
  canonical,
  ogType = 'website',
  ogImage,
  jsonLd,
  noIndex = false,
  article,
}: SEOProps) => {
  const baseUrl = getBaseUrl();
  const fullTitle = title ? `${title} | ${siteConfig.name}` : siteConfig.title;
  const canonicalUrl = canonical ? `${baseUrl}${canonical}` : baseUrl;
  const imageUrl = ogImage || `${baseUrl}${siteConfig.ogImage}`;

  useEffect(() => {
    document.title = fullTitle;

    const setMetaTag = (property: string, content: string, isProperty = false) => {
      const attribute = isProperty ? 'property' : 'name';
      let element = document.querySelector(`meta[${attribute}="${property}"]`);
      if (!element) {
        element = document.createElement('meta');
        element.setAttribute(attribute, property);
        document.head.appendChild(element);
      }
      element.setAttribute('content', content);
    };

    const setLinkTag = (rel: string, href: string) => {
      let element = document.querySelector(`link[rel="${rel}"]`) as HTMLLinkElement;
      if (!element) {
        element = document.createElement('link');
        element.setAttribute('rel', rel);
        document.head.appendChild(element);
      }
      element.href = href;
    };

    // robots
    setMetaTag('robots', noIndex ? 'noindex, nofollow' : 'index, follow');

    // last-modified
    setMetaTag('last-modified', new Date().toISOString());

    // Basic
    setMetaTag('description', description);
    setMetaTag('keywords', siteConfig.keywords);
    setLinkTag('canonical', canonicalUrl);

    // Open Graph
    setMetaTag('og:type', ogType, true);
    setMetaTag('og:title', fullTitle, true);
    setMetaTag('og:description', description, true);
    setMetaTag('og:url', canonicalUrl, true);
    setMetaTag('og:image', imageUrl, true);
    setMetaTag('og:site_name', siteConfig.name, true);
    setMetaTag('og:locale', siteConfig.locale, true);

    // Twitter Card
    setMetaTag('twitter:card', 'summary_large_image');
    setMetaTag('twitter:title', fullTitle);
    setMetaTag('twitter:description', description);
    setMetaTag('twitter:image', imageUrl);

    // Article
    if (article && ogType === 'article') {
      if (article.publishedTime) setMetaTag('article:published_time', article.publishedTime, true);
      if (article.modifiedTime) setMetaTag('article:modified_time', article.modifiedTime, true);
      if (article.author) setMetaTag('article:author', article.author, true);
      document.querySelectorAll('meta[property="article:tag"]').forEach(el => el.remove());
      if (article.tags) {
        article.tags.forEach(tag => {
          const tagElement = document.createElement('meta');
          tagElement.setAttribute('property', 'article:tag');
          tagElement.setAttribute('content', tag);
          document.head.appendChild(tagElement);
        });
      }
    }
  }, [fullTitle, description, canonicalUrl, imageUrl, ogType, article, noIndex]);

  if (jsonLd) {
    return (
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
      />
    );
  }

  return null;
};
